
import { useState, useEffect, useCallback } from 'react';
import { UrgentBooking } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import mockUrgentBookings from './urgent-bookings.json';

export const useUrgentBookings = (isAdmin: boolean, currentUserId: string, useMockData: boolean) => {
  const [bookings, setBookings] = useState<UrgentBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { notify } = useToast();
  const { addLog } = useLogger();

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    addLog("Starting urgent bookings data fetch.", LogLevel.INFO, LogCategory.DATA);

    if (useMockData) {
      addLog("Using mock data for urgent bookings.", LogLevel.INFO, LogCategory.DATA);
      setBookings(mockUrgentBookings as UrgentBooking[]);
      setIsLoading(false);
      return;
    }

    let endpoint = `${API_BASE_URL}/api/urgent-bookings`;
    if (!isAdmin && currentUserId) {
      endpoint += `?userId=${currentUserId}`;
    }

    try {
      const response = await fetch(endpoint, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Real API failed. Status: ${response.status}`);
      }
      const data = await response.json();
      setBookings(data as UrgentBooking[]);
      addLog(`Fetched ${data.length} urgent bookings from REAL API.`, LogLevel.SUCCESS, LogCategory.API);
    } catch (err: unknown) {
      const errorMessage = `Failed to load urgent bookings: ${(err as Error).message}`;
      setError("Could not load real data. Falling back to mock data.");
      addLog(errorMessage, LogLevel.ERROR, LogCategory.API);
      notify("Could not load real data. Falling back to mock data.", 'warn');
      // Fallback to mock data on API error
      setBookings(mockUrgentBookings as UrgentBooking[]);
      addLog("Fell back to mock data for urgent bookings.", LogLevel.WARN, LogCategory.DATA);
    }
    setIsLoading(false);
  }, [isAdmin, currentUserId, useMockData, notify, addLog]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const deleteBooking = async (id: string) => {
    if (useMockData) {
        setBookings(prevBookings => prevBookings.filter(b => b.id !== id));
        notify('Deleted booking from mock data.', 'info');
        return;
    }
    
    const originalBookings = [...bookings];
    setBookings(bookings.filter((booking) => booking.id !== id));

    try {
      const response = await fetch(`${API_BASE_URL}/api/urgent-bookings/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        setBookings(originalBookings);
        throw new Error('Failed to delete booking from API');
      }
      addLog(`Booking ${id} deleted successfully.`, LogLevel.INFO, LogCategory.DATA);
      notify('Booking deleted successfully', 'success');
    } catch (err: unknown) {
      const errorMessage = `Failed to delete booking: ${(err as Error).message}`;
      setError(errorMessage);
      addLog(errorMessage, LogLevel.ERROR, LogCategory.DATA);
      notify(errorMessage, 'error');
      setBookings(originalBookings);
    }
  };

  return { bookings, isLoading, error, refetch: fetchBookings, deleteBooking };
};
